// AUTO-ESTRATTO da TeamDetail.jsx (refactor strutturale Fase 10.01) — nessun cambio di logica/UI/testid.
import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { trPositionShort } from "@/lib/pesTranslations";
import { formatMln } from "./helpers";

export function LegacyWageEditor({ entryId, teamId, currentWage, applied, isAdmin, onSaved }) {
    const [editing, setEditing] = useState(false);
    const [val, setVal] = useState("");
    const [busy, setBusy] = useState(false);
    const color = applied ? "rgb(var(--tc-10b981))" : "rgb(var(--tc-8f95a3))";

    if (!isAdmin || !entryId) {
        return <span style={{ color }}>{formatMln(currentWage)}</span>;
    }
    if (!editing) {
        return (
            <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setVal(currentWage ? (currentWage / 1e6).toString() : ""); setEditing(true); }}
                className="group inline-flex items-center gap-1 hover:underline decoration-dotted decoration-t2bb6ff"
                title="Modifica stipendio legacy (admin)"
                style={{ color }}
                data-testid={`edit-legacy-${entryId}`}
            >
                {formatMln(currentWage)}
                <span className="text-t2bb6ff text-[9px] opacity-50 group-hover:opacity-100">✎</span>
            </button>
        );
    }
    const save = async () => {
        const n = parseFloat(val);
        if (isNaN(n) || n < 0) { setEditing(false); return; }
        setBusy(true);
        try {
            await api.patch(`/teams/${teamId}/roster/${entryId}/legacy-wage`, { wage_mln: n });
            setEditing(false);
            onSaved?.();
        } catch (e) {
            alert(e.response?.data?.detail || "Errore salvataggio");
        } finally { setBusy(false); }
    };
    return (
        <span className="inline-flex items-center gap-1" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
            <input
                autoFocus
                type="number"
                step="0.1"
                min="0"
                value={val}
                disabled={busy}
                onChange={(e) => setVal(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
                className="w-16 bg-t0e1014 border border-t2bb6ff text-tffffff text-right op-mono text-xs px-1 py-0.5"
                placeholder="mln"
                data-testid={`legacy-input-${entryId}`}
            />
            <button type="button" onClick={save} disabled={busy} className="text-t10b981" data-testid={`legacy-save-${entryId}`}>✓</button>
        </span>
    );
}

// Status/tag modificabili dall'admin (lista fissa).
export const STATUS_OPTIONS = [
    { code: "NFC", label: "Faccia Nuova (NFC)" },
    { code: "NFC2", label: "Faccia Nuova 2ª sessione (NFC2)" },
    { code: "PRIM", label: "Primavera (PRIM)" },
    { code: "Ugly", label: "Settore Giovanile (Ugly)" },
    { code: "SVI", label: "Svincolato · blocco stagionale (SVI)" },
    { code: "SVI2", label: "Svincolato 2ª sessione · max 2 (SVI2)" },
];

// Editor admin per correggere errori di import: sposta una voce di rosa in
// un'altra squadra (porta con sé tutto) o la rimuove (doppioni). Nessun soldo,
// nessun annuncio. Mostra anche l'avviso "presente anche in: ..." (duplicati).
export function AdminRosterEditor({ p, teamId, isAdmin, onChanged, compact = false }) {
    const [open, setOpen] = useState(false);
    const [teams, setTeams] = useState([]);
    const [dest, setDest] = useState("");
    const [busy, setBusy] = useState(false);
    const [stat, setStat] = useState(() => new Set(p.statuses || []));
    const [nameVal, setNameVal] = useState(p.name || "");
    const [pesIdVal, setPesIdVal] = useState("");
    const [tmVal, setTmVal] = useState(p.tm_link || "");
    const [ageVal, setAgeVal] = useState(p.age != null ? String(p.age) : "");
    const [imgVal, setImgVal] = useState(p.tm_image || "");
    const [refetch, setRefetch] = useState(false);

    if (!isAdmin || !p.entry_id) return null;
    const dups = p.dup_in_teams || [];

    const openModal = async (e) => {
        e?.preventDefault?.(); e?.stopPropagation?.();
        setStat(new Set(p.statuses || []));
        setNameVal(p.name || "");
        setTmVal(p.tm_link || "");
        setAgeVal(p.age != null ? String(p.age) : "");
        setImgVal(p.tm_image || "");
        setRefetch(false);
        setOpen(true);
        if (teams.length === 0) {
            try { const { data } = await api.get("/teams"); setTeams(data || []); } catch { /* ignore */ }
        }
    };

    const toggleStat = (code) => {
        setStat((prev) => {
            const next = new Set(prev);
            if (next.has(code)) next.delete(code);
            else next.add(code);
            return next;
        });
    };

    const saveStatuses = async () => {
        setBusy(true);
        try {
            await api.put(`/teams/${teamId}/roster/${p.entry_id}/admin-statuses`, { statuses: Array.from(stat) });
            setOpen(false); onChanged?.();
        } catch (err) { alert(err.response?.data?.detail || "Errore salvataggio stato"); }
        finally { setBusy(false); }
    };
    const move = async () => {
        if (!dest) return;
        setBusy(true);
        try {
            await api.post(`/teams/${teamId}/roster/${p.entry_id}/admin-move`, { target_team_id: dest });
            setOpen(false); setDest(""); onChanged?.();
        } catch (err) { alert(err.response?.data?.detail || "Errore spostamento"); }
        finally { setBusy(false); }
    };
    const remove = async () => {
        if (!window.confirm(`Rimuovere "${p.name}" dalla rosa?\n\nÈ una correzione errore: nessun rimborso, nessun annuncio.`)) return;
        setBusy(true);
        try {
            await api.delete(`/teams/${teamId}/roster/${p.entry_id}/admin-remove`);
            setOpen(false); onChanged?.();
        } catch (err) { alert(err.response?.data?.detail || "Errore rimozione"); }
        finally { setBusy(false); }
    };
    const saveName = async () => {
        const v = (nameVal || "").trim();
        if (!v || v === (p.name || "")) return;
        setBusy(true);
        try {
            await api.put(`/teams/${teamId}/roster/${p.entry_id}/admin-name`, { name: v });
            setOpen(false); onChanged?.();
        } catch (err) { alert(err.response?.data?.detail || "Errore salvataggio nome"); }
        finally { setBusy(false); }
    };
    const savePesId = async () => {
        const v = (pesIdVal || "").trim();
        if (!/^\d+$/.test(v)) { alert("Inserisci un ID PESDB numerico valido."); return; }
        if (!window.confirm(`Agganciare l'ID PESDB ${v} a "${p.name}"?\n\nScarico i dati da PESDB e aggiorno scheda/VG/ruolo. Se il giocatore aveva un altro id, viene ri-agganciato (e portato in produzione col Ponte).`)) return;
        setBusy(true);
        try {
            const { data } = await api.put(`/teams/${teamId}/roster/${p.entry_id}/admin-pesid`, { pes_id: Number(v) });
            setOpen(false); onChanged?.();
            alert(`Agganciato: ${data.name} (VG ${data.overall ?? "?"} · ${data.position ?? "?"})`);
        } catch (err) { alert(err.response?.data?.detail || "Errore aggancio ID PESDB"); }
        finally { setBusy(false); }
    };
    const unlinkPesdb = async () => {
        if (!window.confirm(`Scollegare "${p.name}" dalla scheda PESDB?\n\nIl giocatore resta in rosa col proprio nome/età/storico ma senza scheda PESDB (utile quando è stato agganciato per errore alla scheda di un altro giocatore con lo stesso nome). Poi potrai assegnargli Ruolo/VG a mano.`)) return;
        setBusy(true);
        try {
            await api.post(`/teams/${teamId}/roster/${p.entry_id}/admin-unlink-pesdb`);
            setOpen(false); onChanged?.();
        } catch (err) { alert(err.response?.data?.detail || "Errore scollegamento PESDB"); }
        finally { setBusy(false); }
    };
    const saveTm = async () => {
        const link = (tmVal || "").trim();
        const ageStr = (ageVal || "").trim();
        const img = (imgVal || "").trim();
        const body = { refetch };
        if (link !== (p.tm_link || "")) body.tm_link = link;
        if (img !== (p.tm_image || "")) body.tm_image = img;
        if (ageStr !== "") {
            const n = parseInt(ageStr, 10);
            if (Number.isNaN(n) || n < 14 || n > 60) { alert("Età non valida (14–60)."); return; }
            if (n !== (p.age ?? null)) body.tm_age = n;
        }
        if (body.tm_link === undefined && body.tm_image === undefined && body.tm_age === undefined && !refetch) {
            alert("Niente da aggiornare: cambia il link, la foto o l'età."); return;
        }
        setBusy(true);
        try {
            const { data } = await api.put(`/teams/${teamId}/roster/${p.entry_id}/admin-tm`, body);
            setOpen(false); onChanged?.();
            if (data.fetched?.error) alert(`Salvato. Nota: rilettura da TM non riuscita (${data.fetched.error}). In produzione lo scraping è bloccato: usa l'età manuale.`);
        } catch (err) { alert(err.response?.data?.detail || "Errore salvataggio TM"); }
        finally { setBusy(false); }
    };

    return (
        <span className="inline-flex items-center gap-1" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
            {dups.length > 0 && (
                <span
                    className="op-mono text-[9px] px-1 py-0.5 border border-tff3b30 text-tff3b30 uppercase"
                    title={`Stesso giocatore anche in: ${dups.join(", ")}`}
                    data-testid={`dup-badge-${p.entry_id}`}
                >
                    ⚠ Doppio
                </span>
            )}
            <button
                type="button"
                onClick={openModal}
                className={`inline-flex items-center gap-1 border border-t8f95a3/40 text-t8f95a3 hover:border-tffffff hover:text-tffffff transition-colors op-mono uppercase tracking-wider ${compact ? "text-[9px] px-1.5 py-0.5" : "w-full justify-center text-xs py-2"}`}
                title="Editor admin: sposta o rimuovi"
                data-testid={`admin-edit-${p.entry_id}`}
            >
                ✎ {compact ? "Edit" : "Sposta / Rimuovi (admin)"}
            </button>

            {open && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
                    onClick={() => setOpen(false)}
                    data-testid={`admin-edit-modal-${p.entry_id}`}
                >
                    <div className="op-card bg-t0e1014 border border-t22252a p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
                        <div className="op-label text-t2bb6ff mb-1">// EDITOR ROSA (ADMIN)</div>
                        <div className="op-heading text-xl text-tffffff mb-1">{p.name}</div>
                        <div className="op-mono text-[11px] text-t8f95a3 mb-4">
                            {trPositionShort(p.position) || "—"} · VG {p.vg ?? "—"} · contratto {p.contract_years ?? "—"} anni
                        </div>

                        {dups.length > 0 && (
                            <div className="border border-tff3b30 bg-tff3b30/10 text-tff3b30 op-mono text-[11px] p-2 mb-4">
                                ⚠ Doppione: lo stesso giocatore è anche in {dups.join(", ")}. Per i doppioni usa "Rimuovi".
                            </div>
                        )}
                        {(p.is_loaned_out || p.is_on_loan) && (
                            <div className="border border-tff6b00 bg-tff6b00/10 text-tff6b00 op-mono text-[11px] p-2 mb-4">
                                ⚠ Questo giocatore è in prestito: spostarlo lascerà appesi i riferimenti del prestito sull'altra squadra.
                            </div>
                        )}

                        <div className="op-label text-[10px] mb-2">Sposta in un'altra squadra</div>
                        <div className="flex items-center gap-2 mb-5">
                            <select
                                value={dest}
                                onChange={(e) => setDest(e.target.value)}
                                disabled={busy}
                                className="flex-1 bg-t0e1014 border border-t22252a text-tffffff op-mono text-sm px-3 py-2 focus:border-t2bb6ff outline-none"
                                data-testid={`admin-move-select-${p.entry_id}`}
                            >
                                <option value="">— scegli squadra —</option>
                                {teams.filter((t) => t.id !== teamId).map((t) => (
                                    <option key={t.id} value={t.id}>{t.name}</option>
                                ))}
                            </select>
                            <button
                                type="button"
                                onClick={move}
                                disabled={busy || !dest}
                                className="op-btn border border-t2bb6ff text-t2bb6ff hover:bg-t2bb6ff hover:text-black px-4 py-2 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                data-testid={`admin-move-confirm-${p.entry_id}`}
                            >
                                Sposta
                            </button>
                        </div>

                        {/* Stato / Tag (lista fissa) */}
                        <div className="border-t border-t22252a pt-4 mb-5">
                            <div className="op-label text-[10px] mb-2">Stato / Tag</div>
                            <div className="grid grid-cols-2 gap-2">
                                {STATUS_OPTIONS.map((opt) => {
                                    const on = stat.has(opt.code);
                                    return (
                                        <button
                                            key={opt.code}
                                            type="button"
                                            onClick={() => toggleStat(opt.code)}
                                            className={`flex items-center gap-2 px-3 py-2 border text-left op-mono text-[11px] transition-colors ${
                                                on
                                                    ? "border-t2bb6ff bg-t2bb6ff/10 text-tffffff"
                                                    : "border-t22252a text-t8f95a3 hover:border-t3a3f47"
                                            }`}
                                            data-testid={`admin-status-${opt.code}-${p.entry_id}`}
                                        >
                                            <span className={`w-3 h-3 border flex-shrink-0 ${on ? "bg-t2bb6ff border-t2bb6ff" : "border-t5c6370"}`}>
                                                {on && <span className="block text-black text-[9px] leading-3 text-center">✓</span>}
                                            </span>
                                            {opt.label}
                                        </button>
                                    );
                                })}
                            </div>
                            <button
                                type="button"
                                onClick={saveStatuses}
                                disabled={busy}
                                className="op-btn border border-t7ce38b text-t7ce38b hover:bg-t7ce38b hover:text-black px-4 py-2 mt-3 disabled:opacity-40 transition-colors"
                                data-testid={`admin-status-save-${p.entry_id}`}
                            >
                                Salva stato
                            </button>
                            <span className="op-mono text-[10px] text-t5c6370 ml-3">Ugly sposta il giocatore nel settore giovanile</span>
                        </div>


                        {/* Correzione nome */}
                        <div className="border-t border-t22252a pt-4 mb-5">
                            <div className="op-label text-[10px] mb-2">Nome giocatore</div>
                            <div className="flex items-center gap-2">
                                <input
                                    type="text"
                                    value={nameVal}
                                    onChange={(e) => setNameVal(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === "Enter") saveName(); }}
                                    maxLength={80}
                                    className="flex-1 bg-t0a0b0d border border-t22252a focus:border-t2bb6ff outline-none text-tffffff text-sm px-3 py-2 op-mono"
                                    placeholder="Nome / cognome corretto"
                                    data-testid={`admin-name-input-${p.entry_id}`}
                                />
                                <button
                                    type="button"
                                    onClick={saveName}
                                    disabled={busy || !nameVal.trim() || nameVal.trim() === (p.name || "")}
                                    className="op-btn border border-t2bb6ff text-t2bb6ff hover:bg-t2bb6ff hover:text-black px-4 py-2 disabled:opacity-40 transition-colors"
                                    data-testid={`admin-name-save-${p.entry_id}`}
                                >
                                    Salva nome
                                </button>
                            </div>
                            {p.pes_id != null && (
                                <p className="op-mono text-[10px] text-t5c6370 mt-2 leading-relaxed">
                                    Collegato a PESDB: la correzione aggiorna anche la scheda e la ricerca (per tutte le squadre).
                                </p>
                            )}
                        </div>

                        {/* Aggancia ID PESDB a mano */}
                        <div className="border-t border-t22252a pt-4 mb-5">
                            <div className="op-label text-[10px] mb-2">Aggancia ID PESDB a mano</div>
                            <div className="flex items-center gap-2">
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    value={pesIdVal}
                                    onChange={(e) => setPesIdVal(e.target.value.replace(/[^\d]/g, ""))}
                                    onKeyDown={(e) => { if (e.key === "Enter") savePesId(); }}
                                    className="flex-1 bg-t0a0b0d border border-t22252a focus:border-te8ff00 outline-none text-tffffff text-sm px-3 py-2 op-mono"
                                    placeholder="es. 16837662"
                                    data-testid={`admin-pesid-input-${p.entry_id}`}
                                />
                                <button
                                    type="button"
                                    onClick={savePesId}
                                    disabled={busy || !pesIdVal.trim()}
                                    className="op-btn border border-te8ff00 text-te8ff00 hover:bg-te8ff00 hover:text-black px-4 py-2 disabled:opacity-40 transition-colors whitespace-nowrap"
                                    data-testid={`admin-pesid-save-${p.entry_id}`}
                                >
                                    Aggancia
                                </button>
                            </div>
                            <p className="op-mono text-[10px] text-t5c6370 mt-2 leading-relaxed">
                                Incolla il numero ID dalla pagina PESDB del giocatore. Scarico i dati e aggiorno scheda/VG/ruolo.
                                {p.has_pes_id ? " Se l'id era diverso, viene ri-agganciato (old→new) e portato in produzione col Ponte." : " Utile per i giovani senza PESDB."}
                            </p>
                        </div>

                        {/* Scollega da PESDB (assegnazione errata) */}
                        {p.has_pes_id && (
                            <div className="border-t border-t22252a pt-4 mb-5">
                                <div className="op-label text-[10px] mb-2">Scheda PESDB</div>
                                <button
                                    type="button"
                                    onClick={unlinkPesdb}
                                    disabled={busy}
                                    className="op-btn border border-tff9800 text-tff9800 hover:bg-tff9800 hover:text-black px-4 py-2 disabled:opacity-40 transition-colors"
                                    data-testid={`admin-unlink-pesdb-${p.entry_id}`}
                                >
                                    ⛓️‍💥 Scollega da PESDB
                                </button>
                                <p className="op-mono text-[10px] text-t5c6370 mt-2 leading-relaxed">
                                    Usa quando il giocatore è agganciato per errore alla scheda di un omonimo
                                    (es. un Ugly che porta la scheda di un altro). Resta in rosa senza scheda PESDB;
                                    poi potrai assegnargli Ruolo/VG a mano.
                                </p>
                            </div>
                        )}

                        {/* Transfermarkt: link manuale + età manuale */}
                        <div className="border-t border-t22252a pt-4 mb-5">
                            <div className="op-label text-[10px] mb-2">Transfermarkt · link, età & foto</div>
                            <input
                                type="text"
                                value={tmVal}
                                onChange={(e) => setTmVal(e.target.value)}
                                className="w-full bg-t0a0b0d border border-t22252a focus:border-t00bfff outline-none text-tffffff text-sm px-3 py-2 op-mono mb-2"
                                placeholder="https://www.transfermarkt.it/.../profil/spieler/..."
                                data-testid={`admin-tm-link-input-${p.entry_id}`}
                            />
                            <div className="flex items-center gap-2 mb-2">
                                {imgVal.trim() ? (
                                    <img
                                        src={imgVal.trim()}
                                        alt="anteprima"
                                        className="w-9 h-9 rounded-full object-cover border border-t22252a shrink-0"
                                        onError={(e) => { e.currentTarget.style.opacity = "0.25"; }}
                                        data-testid={`admin-tm-photo-preview-${p.entry_id}`}
                                    />
                                ) : (
                                    <div className="w-9 h-9 rounded-full border border-t22252a flex items-center justify-center op-mono text-[9px] text-t5c6370 shrink-0">foto</div>
                                )}
                                <input
                                    type="text"
                                    value={imgVal}
                                    onChange={(e) => setImgVal(e.target.value)}
                                    className="flex-1 bg-t0a0b0d border border-t22252a focus:border-t00bfff outline-none text-tffffff text-sm px-3 py-2 op-mono"
                                    placeholder="URL foto (headshot) — incolla l'indirizzo immagine"
                                    data-testid={`admin-tm-image-input-${p.entry_id}`}
                                />
                            </div>
                            <div className="flex items-center gap-2">
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    value={ageVal}
                                    onChange={(e) => setAgeVal(e.target.value.replace(/[^\d]/g, ""))}
                                    className="w-24 bg-t0a0b0d border border-t22252a focus:border-t00bfff outline-none text-tffffff text-sm px-3 py-2 op-mono"
                                    placeholder="Età"
                                    data-testid={`admin-tm-age-input-${p.entry_id}`}
                                />
                                <label className="flex items-center gap-1.5 op-mono text-[10px] text-t8f95a3 cursor-pointer select-none">
                                    <input type="checkbox" checked={refetch} onChange={(e) => setRefetch(e.target.checked)} data-testid={`admin-tm-refetch-${p.entry_id}`} />
                                    Rileggi da TM
                                </label>
                                <button
                                    type="button"
                                    onClick={saveTm}
                                    disabled={busy}
                                    className="op-btn border border-t00bfff text-t00bfff hover:bg-t00bfff hover:text-black px-4 py-2 disabled:opacity-40 transition-colors ml-auto"
                                    data-testid={`admin-tm-save-${p.entry_id}`}
                                >
                                    Salva TM
                                </button>
                            </div>
                            <p className="op-mono text-[10px] text-t5c6370 mt-2 leading-relaxed">
                                L'<strong className="text-tc9ccd3">età manuale</strong> e l'<strong className="text-tc9ccd3">URL foto</strong> funzionano anche in produzione (nessuno scraping).
                                Per la foto: apri il profilo Transfermarkt/PESDB, tasto destro sulla foto → «Copia indirizzo immagine» e incolla qui.
                                "Rileggi da TM" invece scarica foto/età/valore dal link solo dove lo scraping è consentito (preview).
                            </p>
                        </div>

                        <div className="border-t border-t22252a pt-4 flex items-center justify-between">
                            <button
                                type="button"
                                onClick={remove}
                                disabled={busy}
                                className="op-btn border border-tff3b30 text-tff3b30 hover:bg-tff3b30 hover:text-black px-4 py-2 disabled:opacity-40 transition-colors"
                                data-testid={`admin-remove-confirm-${p.entry_id}`}
                            >
                                🗑 Rimuovi dalla rosa
                            </button>
                            <button type="button" onClick={() => setOpen(false)} className="op-btn op-btn-ghost text-t8f95a3">
                                Chiudi
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </span>
    );
}


// Admin: aggiunge / reinserisce un giocatore in rosa (anche solo-Transfermarkt, senza PESDB).
export function AdminAddPlayer({ teamId, teamName, onAdded }) {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({ name: "", tm_link: "", pes_id: "", position: "", vg: "", age: "", is_ugly: false, price: "", contract_years: "" });
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async () => {
        const name = form.name.trim();
        if (!name) { alert("Il nome è obbligatorio."); return; }
        const body = { name, is_ugly: form.is_ugly };
        if (form.tm_link.trim()) body.tm_link = form.tm_link.trim();
        if (form.pes_id.trim()) body.pes_id = parseInt(form.pes_id, 10);
        if (form.position.trim()) body.position = form.position.trim().toUpperCase();
        if (form.vg.trim()) body.vg = parseInt(form.vg, 10);
        if (form.age.trim()) body.age = parseInt(form.age, 10);
        if (form.price.trim()) body.price = Math.round(parseFloat(form.price) * 1e6);
        if (form.contract_years.trim()) body.contract_years = parseInt(form.contract_years, 10);
        setBusy(true);
        try {
            await api.post(`/teams/${teamId}/roster/admin-add`, body);
            setOpen(false);
            setForm({ name: "", tm_link: "", pes_id: "", position: "", vg: "", age: "", is_ugly: false, price: "", contract_years: "" });
            onAdded?.();
        } catch (err) { alert(err.response?.data?.detail || "Errore aggiunta giocatore"); }
        finally { setBusy(false); }
    };

    return (
        <div className="mb-4">
            <button
                type="button"
                onClick={() => setOpen(true)}
                className="op-btn border border-t7ce38b/50 text-t7ce38b hover:bg-t7ce38b hover:text-black px-4 py-2 transition-colors op-mono text-xs uppercase tracking-wider"
                data-testid="admin-add-player-btn"
            >
                ＋ Aggiungi giocatore (admin)
            </button>

            {open && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setOpen(false)} data-testid="admin-add-player-modal">
                    <div className="op-card bg-t0e1014 border border-t22252a p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
                        <div className="op-label text-t7ce38b mb-1">// AGGIUNGI GIOCATORE</div>
                        <div className="op-mono text-[11px] text-t8f95a3 mb-4">In rosa: <span className="text-tffffff">{teamName}</span></div>

                        <label className="op-label text-[10px] block mb-1">Nome *</label>
                        <input type="text" value={form.name} onChange={(e) => set("name", e.target.value)} maxLength={80}
                            className="w-full bg-t0a0b0d border border-t22252a focus:border-t7ce38b outline-none text-tffffff text-sm px-3 py-2 op-mono mb-3"
                            placeholder="Nome / cognome" data-testid="add-player-name" />

                        <label className="op-label text-[10px] block mb-1">Link Transfermarkt (opzionale)</label>
                        <input type="text" value={form.tm_link} onChange={(e) => set("tm_link", e.target.value)}
                            className="w-full bg-t0a0b0d border border-t22252a focus:border-t00bfff outline-none text-tffffff text-sm px-3 py-2 op-mono mb-3"
                            placeholder="https://www.transfermarkt.it/..." data-testid="add-player-tm" />

                        <div className="grid grid-cols-3 gap-2 mb-3">
                            <div>
                                <label className="op-label text-[10px] block mb-1">Ruolo</label>
                                <input type="text" value={form.position} onChange={(e) => set("position", e.target.value)} maxLength={8}
                                    className="w-full bg-t0a0b0d border border-t22252a focus:border-t2bb6ff outline-none text-tffffff text-sm px-2 py-2 op-mono"
                                    placeholder="es. CF" data-testid="add-player-position" />
                            </div>
                            <div>
                                <label className="op-label text-[10px] block mb-1">VG</label>
                                <input type="text" inputMode="numeric" value={form.vg} onChange={(e) => set("vg", e.target.value.replace(/[^\d]/g, ""))}
                                    className="w-full bg-t0a0b0d border border-t22252a focus:border-t2bb6ff outline-none text-tffffff text-sm px-2 py-2 op-mono"
                                    placeholder="79" data-testid="add-player-vg" />
                            </div>
                            <div>
                                <label className="op-label text-[10px] block mb-1">Età</label>
                                <input type="text" inputMode="numeric" value={form.age} onChange={(e) => set("age", e.target.value.replace(/[^\d]/g, ""))}
                                    className="w-full bg-t0a0b0d border border-t22252a focus:border-t2bb6ff outline-none text-tffffff text-sm px-2 py-2 op-mono"
                                    placeholder="24" data-testid="add-player-age" />
                            </div>
                        </div>

                        <label className="op-label text-[10px] block mb-1">ID PESDB (opzionale)</label>
                        <input type="text" inputMode="numeric" value={form.pes_id} onChange={(e) => set("pes_id", e.target.value.replace(/[^\d]/g, ""))}
                            className="w-full bg-t0a0b0d border border-t22252a focus:border-te8ff00 outline-none text-tffffff text-sm px-3 py-2 op-mono mb-3"
                            placeholder="lascia vuoto per un giocatore solo-Transfermarkt" data-testid="add-player-pesid" />

                        <label className="flex items-center gap-2 op-mono text-[11px] text-t8f95a3 cursor-pointer select-none mb-4">
                            <input type="checkbox" checked={form.is_ugly} onChange={(e) => set("is_ugly", e.target.checked)} data-testid="add-player-ugly" />
                            Settore Ugly (giovanile)
                        </label>

                        <div className="border-t border-t22252a pt-3 mb-3">
                            <div className="op-label text-[10px] text-te8ff00 mb-2">// Acquisto (opzionale)</div>
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className="op-label text-[10px] block mb-1">Prezzo (mln)</label>
                                    <input type="text" inputMode="decimal" value={form.price}
                                        onChange={(e) => set("price", e.target.value.replace(/[^\d.]/g, ""))}
                                        className="w-full bg-t0a0b0d border border-t22252a focus:border-te8ff00 outline-none text-tffffff text-sm px-3 py-2 op-mono"
                                        placeholder="es. 10" data-testid="add-player-price" />
                                </div>
                                <div>
                                    <label className="op-label text-[10px] block mb-1">Contratto (anni)</label>
                                    <input type="text" inputMode="numeric" value={form.contract_years}
                                        onChange={(e) => set("contract_years", e.target.value.replace(/[^\d]/g, ""))}
                                        className="w-full bg-t0a0b0d border border-t22252a focus:border-te8ff00 outline-none text-tffffff text-sm px-3 py-2 op-mono"
                                        placeholder="es. 3" data-testid="add-player-contract-years" />
                                </div>
                            </div>
                            <p className="op-mono text-[10px] text-t5c6370 mt-2 leading-relaxed">
                                Se inserisci un prezzo: scala il budget della squadra, accredita la Cassa OP e registra l'importo nello storico del giocatore. Lascia vuoto = aggiunta gratuita.
                            </p>
                        </div>

                        <div className="flex items-center justify-between">
                            <button type="button" onClick={submit} disabled={busy || !form.name.trim()}
                                className="op-btn border border-t7ce38b text-t7ce38b hover:bg-t7ce38b hover:text-black px-4 py-2 disabled:opacity-40 transition-colors"
                                data-testid="add-player-submit">
                                {busy ? "..." : "Aggiungi"}
                            </button>
                            <button type="button" onClick={() => setOpen(false)} className="op-btn op-btn-ghost text-t8f95a3">Chiudi</button>
                        </div>
                        <p className="op-mono text-[10px] text-t5c6370 mt-3 leading-relaxed">
                            Per un giocatore presente solo su Transfermarkt lascia vuoto l'ID PESDB: comparirà coi dati TM (età inclusa).
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
}


